/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import update from 'immutability-helper';
import { cloneDeep as _cloneDeep, isEqual as _isEqual } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@mui/material';

import { HUSpin } from '../../../../../../../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../../../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../../../../../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../../../../../../i18n';
import { ClusterUpdateRequestBody } from '../../../../../../../../types/cluster/ClusterUpdateRequestBody';
import { PgHba } from '../../../../../../../../types/cluster/PgHba/PgHba';
import { PgHbaAuthenticationMethod } from '../../../../../../../../types/cluster/PgHba/PgHbaAuthenticationMethod';
import { PgHbaConnectionTypes } from '../../../../../../../../types/cluster/PgHba/PgHbaConnectionTypes';
import { PgHbaDatabaseSpecifier } from '../../../../../../../../types/cluster/PgHba/PgHbaDatabaseSpecifier';
import { PgHbaResponsePayload } from '../../../../../../../../types/cluster/PgHba/PgHbaResponsePayload';
import { PgHbaUserSpecifier } from '../../../../../../../../types/cluster/PgHba/PgHbaUserSpecifier';
import { ACLFormValues } from '../../../../../../ClusterPage.types';
import { PgHbaCard } from '../PgHbaCard/PgHbaCard';
import { validationSchema } from '../PgHbaCard/PgHbaCard.consts';
import { usePgHbaListStyles } from './PgHbaList.styles';

export interface Item extends PgHba {
  id: number;
  database: any;
  isAllDatabase: boolean;
  user: any;
  isAllUser: boolean;
}

type PgHbaListProps = {
  updateData: (data: ClusterUpdateRequestBody) => Promise<unknown>;
  data: PgHbaResponsePayload[];
  databaseList: string[];
  userList: string[];
  isEditable: boolean;
};

export const PgHbaList = memo<PgHbaListProps>(props => {
  const { t } = useTranslation(Namespaces.Cluster);
  const classes = usePgHbaListStyles();
  const { updateData, data, databaseList, userList, isEditable } = props;

  {
    const [cards, setCards] = useState<Item[]>([]);
    const [initCards, setInitCards] = useState<Item[]>([]);
    const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);

    useEffect(() => {
      if (data && data.length) {
        const arrWithIds: Item[] = data.map((item, index) => {
          return {
            id: index,
            type: item.type,
            database: typeof item.database === 'string' ? [item.database] : item.database,
            isAllDatabase: typeof item.database === 'string',
            user: typeof item.user === 'string' ? [item.user] : item.user,
            isAllUser: typeof item.user === 'string',
            address: item.address,
            authenticationMethod: item.authenticationMethod,
          };
        });
        setCards(arrWithIds);
        setInitCards(arrWithIds);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isDirty = useMemo(() => {
      return !_isEqual(initCards, cards);
    }, [cards, initCards]);

    const moveItem = useCallback(
      (dragIndex: number, hoverIndex: number) => {
        const dragCard = cards[dragIndex];
        setCards(
          update(cards, {
            $splice: [
              [dragIndex, 1],
              [hoverIndex, 0, dragCard],
            ],
          }),
        );
      },
      [cards],
    );

    const removeItem = useCallback(
      (id: number) => {
        const newArr: Item[] = [...cards.filter(el => el.id !== id)];
        setCards(newArr);
      },
      [cards],
    );

    const createItem = useCallback(() => {
      const newArr = [
        ...cards,
        {
          id: new Date().getTime(),
          type: PgHbaConnectionTypes.Default,
          database: [PgHbaDatabaseSpecifier.All],
          isAllDatabase: true,
          user: [PgHbaUserSpecifier.All],
          isAllUser: true,
          address: '0.0.0.0/0',
          authenticationMethod: PgHbaAuthenticationMethod.ScramSha256,
        },
      ];
      setCards(newArr);
    }, [cards]);

    const handleChanges = (values: ACLFormValues) => {
      const newArr = _cloneDeep(cards);
      newArr.map(el => {
        if (el.id === values.id) {
          el.type = values.type;
          el.database = values.database;
          el.isAllDatabase = values.isAllDatabase;
          el.user = values.user;
          el.isAllUser = values.isAllUser;
          el.address = values.address;
          el.authenticationMethod = values.authenticationMethod;
        }
        return el;
      });
      setCards(newArr);
    };

    const handleSubmit = async () => {
      setIsOpenErrorModal(false);
      const pgHba = _cloneDeep(cards);
      const currentCards = _cloneDeep(cards);
      const validateAllForms = pgHba.map(item => {
        return validationSchema.isValid(item).then(isValid => {
          return isValid;
        });
      });

      const res = await Promise.all(validateAllForms);
      const isValid = res.every(item => item);

      if (isValid) {
        setIsProcessing(true);
        pgHba.map((el): Omit<(typeof pgHba)[number], 'id' | 'isAllUser' | 'isAllDatabase'> => {
          if (el.database[0] === PgHbaDatabaseSpecifier.All && el.isAllDatabase) {
            el.database = PgHbaDatabaseSpecifier.All;
          }
          if (el.user[0] === PgHbaUserSpecifier.All && el.isAllUser) {
            el.user = PgHbaUserSpecifier.All;
          }
          el.address = el.address.trim();
          return {
            address: el.address,
            authenticationMethod: el.authenticationMethod,
            database: el.database,
            type: el.type,
            user: el.user,
          };
        });
        try {
          await updateData({
            configuration: {
              pgHba: pgHba.map(entry => ({
                type: entry.type,
                database: entry.database,
                user: entry.user,
                address: entry.address,
                authenticationMethod: entry.authenticationMethod,
              })),
            },
          });
          setInitCards(currentCards);
          setCards(currentCards);
        } catch (e) {
          setIsOpenErrorModal(true);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    return (
      <HUSpin spinning={isProcessing} delay={10} className={classes.root}>
        {cards.map((card, index) => {
          return (
            <Formik<ACLFormValues>
              key={card.id}
              enableReinitialize={true}
              initialValues={{
                id: card.id,
                type: card.type,
                database: card.database,
                isAllDatabase: card.isAllDatabase,
                user: card.user,
                isAllUser: card.isAllUser,
                address: card.address,
                authenticationMethod: card.authenticationMethod,
              }}
              validationSchema={validationSchema}
              validate={handleChanges}
              onSubmit={() => {}}
            >
              {formProps => (
                <PgHbaCard
                  index={index}
                  id={card.id}
                  data={card}
                  databaseList={databaseList}
                  userList={userList}
                  moveItem={moveItem}
                  removeItem={removeItem}
                  {...formProps}
                  isEditable={isEditable}
                />
              )}
            </Formik>
          );
        })}
        <div className={classes.buttons}>
          {isEditable ? (
            <Button onClick={createItem} data-cy="cluster-page-add-new-pghba-line">
              + {t('create_new')}
            </Button>
          ) : null}
          {isDirty && isEditable && (
            <div>
              <Button
                onClick={() => {
                  setCards(initCards);
                }}
                type={'submit'}
                classes={{ root: classes.controlButton }}
                data-cy="cluster-page-acl-tab-reset-button"
              >
                {t('reset')}
              </Button>
              <Button
                onClick={cards.length === 0 ? () => setIsOpenConfirmModal(true) : handleSubmit}
                type={'submit'}
                classes={{ root: classes.controlButton }}
                data-cy="cluster-page-acl-tab-submit-button"
              >
                {t('apply')}
              </Button>
            </div>
          )}
        </div>
        <SimpleConfirmModal
          message={t('details.acl.no_acl_rules_warning')}
          open={isOpenConfirmModal}
          onCancel={() => setIsOpenConfirmModal(false)}
          onConfirm={() => {
            setIsOpenConfirmModal(false);
            void handleSubmit();
          }}
        />
        <SimpleErrorModal open={isOpenErrorModal} onOk={() => setIsOpenErrorModal(false)} />
      </HUSpin>
    );
  }
});
